/**
 * My Tasks Page
 * 
 * Shows all workflow tasks assigned to the current user.
 * Allows users to view, filter, and complete their workflow tasks.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  Clock,
  AlertCircle,
  Filter,
  ChevronRight,
  Workflow,
  Calendar,
  User,
  FileText,
  Play,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import * as workflowApi from '../api/workflows';
import CompleteStepDialog from '../components/workflows/CompleteStepDialog';

// Utility for class names
const cn = (...classes) => classes.filter(Boolean).join(' ');

// Status configurations
const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: Play },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  skipped: { label: 'Skipped', color: 'bg-yellow-100 text-yellow-700', icon: AlertCircle },
  blocked: { label: 'Blocked', color: 'bg-red-100 text-red-700', icon: AlertCircle },
};

// Step type icons
const STEP_TYPE_ICONS = {
  task: FileText,
  approval: CheckCircle2,
  review: FileText,
  decision: ChevronRight,
};

function TaskCard({ task, onComplete }) {
  const statusConfig = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;
  const StepIcon = STEP_TYPE_ICONS[task.step?.step_type] || FileText;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';

  return (
    <Card 
      className={cn(
        "hover:shadow-md transition-shadow cursor-pointer",
        isOverdue && "border-red-300"
      )}
      data-testid={`task-card-${task.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={cn(
              "p-2 rounded-lg",
              task.step?.step_type === 'approval' ? 'bg-green-50' : 'bg-blue-50'
            )}>
              <StepIcon className={cn(
                "w-5 h-5",
                task.step?.step_type === 'approval' ? 'text-green-600' : 'text-blue-600'
              )} />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 truncate">
                {task.step?.name || 'Unnamed Step'}
              </h3>
              
              <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                <Workflow className="w-4 h-4" />
                <span className="truncate">{task.instance?.name || 'Workflow'}</span>
              </div>
              
              {task.step?.description && (
                <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                  {task.step.description}
                </p>
              )}
              
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <Badge className={cn("text-xs", statusConfig.color)}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {statusConfig.label}
                </Badge>
                
                {task.due_date && (
                  <span className={cn(
                    "text-xs flex items-center gap-1",
                    isOverdue ? "text-red-600 font-medium" : "text-gray-500"
                  )}>
                    <Calendar className="w-3 h-3" />
                    {isOverdue ? 'Overdue: ' : 'Due: '}
                    {formatDate(task.due_date)}
                  </span>
                )}
                
                {task.instance?.entity_type && (
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    {task.instance.entity_type}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {(task.status === 'pending' || task.status === 'in_progress') && (
            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onComplete(task);
              }}
              data-testid={`complete-task-btn-${task.id}`}
            >
              Complete
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function TaskSummaryCards({ tasks }) {
  const pending = tasks.filter(t => t.status === 'pending').length;
  const inProgress = tasks.filter(t => t.status === 'in_progress').length;
  const overdue = tasks.filter(t => 
    t.due_date && new Date(t.due_date) < new Date() && t.status !== 'completed'
  ).length;
  const completedToday = tasks.filter(t => {
    if (t.status !== 'completed' || !t.completed_at) return false;
    const completed = new Date(t.completed_at);
    const today = new Date();
    return completed.toDateString() === today.toDateString();
  }).length;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <Card data-testid="pending-tasks-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Clock className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{pending}</p>
              <p className="text-sm text-gray-500">Pending</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card data-testid="in-progress-tasks-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Play className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{inProgress}</p>
              <p className="text-sm text-gray-500">In Progress</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card data-testid="overdue-tasks-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{overdue}</p>
              <p className="text-sm text-gray-500">Overdue</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card data-testid="completed-today-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{completedToday}</p>
              <p className="text-sm text-gray-500">Completed Today</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function MyTasksPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTask, setSelectedTask] = useState(null);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);

  // Fetch tasks
  const { data: tasksData, isLoading, error } = useQuery({
    queryKey: ['my-tasks', statusFilter],
    queryFn: () => workflowApi.getMyTasks(statusFilter === 'all' ? null : statusFilter),
  });

  const tasks = tasksData?.data || [];

  // Complete step mutation
  const completeMutation = useMutation({
    mutationFn: ({ stepInstanceId, completionData }) => 
      workflowApi.completeStep(stepInstanceId, completionData),
    onSuccess: () => {
      queryClient.invalidateQueries(['my-tasks']);
      toast.success('Task completed successfully');
      setShowCompleteDialog(false);
      setSelectedTask(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to complete task');
    },
  });

  const handleCompleteClick = (task) => {
    setSelectedTask(task);
    setShowCompleteDialog(true);
  };

  const handleCompleteSubmit = (completionData) => {
    if (!selectedTask) return;
    completeMutation.mutate({
      stepInstanceId: selectedTask.id,
      completionData,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span>Failed to load tasks: {error.message}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="my-tasks-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Tasks</h1>
          <p className="text-gray-500 mt-1">
            Workflow tasks assigned to you
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]" data-testid="status-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tasks</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <TaskSummaryCards tasks={tasks} />

      {/* Task List */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">
          {statusFilter === 'all' ? 'All Tasks' : `${STATUS_CONFIG[statusFilter]?.label || statusFilter} Tasks`}
          <span className="text-gray-400 font-normal ml-2">({tasks.length})</span>
        </h2>
        
        {tasks.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Workflow className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks found</h3>
              <p className="text-gray-500">
                {statusFilter === 'all' 
                  ? "You don't have any workflow tasks assigned yet."
                  : `No ${statusFilter.replace('_', ' ')} tasks.`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {tasks.map((task) => (
              <TaskCard 
                key={task.id} 
                task={task} 
                onComplete={handleCompleteClick}
              />
            ))}
          </div>
        )}
      </div>

      {/* Complete Step Dialog */}
      {selectedTask && (
        <CompleteStepDialog
          open={showCompleteDialog}
          onOpenChange={setShowCompleteDialog}
          task={selectedTask}
          onComplete={handleCompleteSubmit}
          isLoading={completeMutation.isPending}
        />
      )}
    </div>
  );
}
